import { Grid } from '@mui/material';
import { BoostedBadge } from 'components/BoostedBadge/BoostedBadge';
import { BuyerProtectionModal } from 'components/BuyerProtectionModal/BuyerProtectionModal';
import ChipsItem from 'components/ChipsItem';
import { PriceDropBadge } from 'components/PriceDropBadge/PriceDropBadge';
import ShieldIcon from 'public/icons/icn-shield-bold.svg';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OfferStatus, OfferStatusOption } from 'types/offer.types';
import { ProductType } from 'types/product.types';

import { usePriceDropBadge } from '../../../hooks/usePriceDropBadge';
import { useTrackPriceDropBadgeItemCardView } from '../../../hooks/usePriceDropBadgeTracking';
import { usePriceFormatter } from '../../../hooks/usePriceFormatter';
import { isBumped } from '../../../utils/bumpedItems';
import ShippingInformation from '../ShippingInformation/ShippingInformation';

interface ProductPriceProps {
  product: ProductType;
  buyerProtectionHighlighted: boolean;
  handleBundlingTimeEnd: () => void;
}

export const ProductPrice = ({ product, buyerProtectionHighlighted, handleBundlingTimeEnd }: ProductPriceProps): JSX.Element => {
  const { t } = useTranslation();
  const formatPrice = usePriceFormatter();
  const [isBuyerProtectionModalOpen, setBuyerProtectionModalOpen] = useState(false);
  const hasFreeBuyerProtection = product?.buyerSideFeesPercentage === 0 && product?.buyerSideFeesFixed === 0;
  const hasAcceptedOffer = ([OfferStatusOption.ACCEPTED, OfferStatusOption.COMPLETED] as OfferStatus[]).includes(product?.offer?.status);
  const priceDrop = usePriceDropBadge(product?.priceDrop, hasAcceptedOffer || product?.status === 'sold');
  const bumped = isBumped(product) && product?.status !== 'sold';

  useTrackPriceDropBadgeItemCardView({ id: product?.id, slug: product?.slug, price: product?.price }, priceDrop);

  const hasShippingMethods = product?.shippingOptionsWithTotals && !!Object.keys(product.shippingOptionsWithTotals).length;

  return (
    <Grid item xs={12} className="price-container">
      {/* Main product price display */}
      <h4 className="product-price">
        {hasAcceptedOffer ? (
          <>
            {formatPrice(product?.offer?.offeredPrice)}
            <span className="old-price">{formatPrice(product?.price)}</span>
          </>
        ) : priceDrop ? (
          <>
            {formatPrice(product?.price)}
            <span className="old-price old-price--light">{formatPrice(priceDrop.referencePrice)}</span>
          </>
        ) : (
          formatPrice(product?.price)
        )}
        {(priceDrop || bumped) && (
          <span className="product-badges">
            {priceDrop && <PriceDropBadge percentage={priceDrop.percentage} />}
            {bumped && <BoostedBadge />}
          </span>
        )}
      </h4>
      {/* Shipping information */}

      {!hasShippingMethods && (
        <Grid container className="info-element shipping-info-element">
          {t('components.page-product.product-details.shipping.delivery-not-supported')}
        </Grid>
      )}

      {hasShippingMethods && (
        <ShippingInformation
          shippingOptionsWithTotals={product?.shippingOptionsWithTotals}
          status={product?.status}
          handleBundlingTimeEnd={handleBundlingTimeEnd}
        />
      )}

      {/* Buyer protection fee */}
      {product.buyerProtectionFeeEnabled && hasShippingMethods && (
        <Grid container className="info-element shipping-info-element">
          {buyerProtectionHighlighted && hasFreeBuyerProtection ? (
            <>
              + <ShieldIcon className="shield-icon" />
              {t('components.page-product.product-details.shield.buyer-protection-highlighted.title')}
              <ChipsItem
                title={t('components.page-product.product-details.shield.buyer-protection-highlighted.limited-free')}
                isClickable={false}
                clickableLink={null}
              />
            </>
          ) : (
            <>+ {t('components.page-product.product-details.buyer-protection-fee')}</>
          )}
          &nbsp;&nbsp;
          <BuyerProtectionModal
            isOpen={isBuyerProtectionModalOpen}
            setOpen={setBuyerProtectionModalOpen}
            tooltipButtonClassName="p-0"
            buyerSideFeesPercentage={product.buyerSideFeesPercentage}
            buyerSideFeesFixed={product.buyerSideFeesFixed}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default ProductPrice;
